import { StringOutputParser } from "@langchain/core/output_parsers";
import { PromptTemplate } from "@langchain/core/prompts";
import { parseAiJson } from "./aiJson";
import { getChatModel } from "./llmClient";

export const analyzeContent = async (
  htmlContent,
  setFeedback,
  provider = "openai"
) => {
  const llm = await getChatModel(provider);

  let context = htmlContent;
  let results;

  //console.log("CONTENT: ", context);

  // const format = `{
  //   "Overall Grade": XXX,
  //   "Readability Grade": XXX,
  //   "Topic Authority Grade": XXX,
  //   "Content Value Grade": XXX,
  //   "SEO Grade": XXX,
  //   "Meta Tags": <head>
  //     <title>...</title>
  //     <meta name="description" content="...">
  //     <meta name="keywords" content="...">
  //     <meta name="viewport" content="width=device-width, initial-scale=1">
  //     <link rel="canonical" href="...">
  //     <meta property="og:title" content="...">
  //     <meta property="og:description" content="...">
  //     <meta property="og:image" content="...">
  //     <meta property="og:url" content="...">
  //     <meta name="twitter:card" content="summary_large_image">
  //     <meta name="twitter:title" content="...">
  //     <meta name="twitter:description" content="...">
  //     <meta name="twitter:image" content="...">
  //     <meta name="robots" content="index, follow">
  //   </head>
  // }`;

  const format = `{
        "Overall Grade": <number 1-100>,
        "Readability Grade": <number 1-100>,
        "Topic Authority Grade": <number 1-100>,
        "Content Value Grade": <number 1-100>,
        "SEO Grade": <number 1-100>,
        "Meta Tags": "<head>
        <title>...</title>
        <meta name="description" content="...">
        <meta name="keywords" content="...">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <link rel="canonical" href="...">
        <meta property="og:title" content="...">
        <meta property="og:description" content="...">
        <meta property="og:image" content="...">
        <meta property="og:url" content="...">
        <meta name="twitter:card" content="summary_large_image">
        <meta name="twitter:title" content="...">
        <meta name="twitter:description" content="...">
        <meta name="twitter:image" content="...">
        <meta name="robots" content="index, follow">
      </head>",
        "Improvements": {
          "Overall": [
            {
              "exact_text": "<verbatim snippet copied word-for-word from the article>",
              "location_hint": "<e.g., H2: 'Benefits', paragraph after first H3, list item #3>",
              "reason": "<why this should be improved>",
              "suggested_replacement": "<replacement text the user could paste 1:1>",
              "severity": "low|medium|high"
            }
          ],
          "Readability":  [
            {
              "exact_text": "<verbatim snippet copied word-for-word from the article>",
              "location_hint": "<e.g., H2: 'Benefits', paragraph after first H3, list item #3>",
              "reason": "<why this should be improved>",
              "suggested_replacement": "<replacement text the user could paste 1:1>",
              "severity": "low|medium|high"
           ,
          "Topic Authority":  [
            {
              "exact_text": "<verbatim snippet copied word-for-word from the article>",
              "location_hint": "<e.g., H2: 'Benefits', paragraph after first H3, list item #3>",
              "reason": "<why this should be improved>",
              "suggested_replacement": "<replacement text the user could paste 1:1>",
              "severity": "low|medium|high"
           ,
          "Content Value":  [
            {
              "exact_text": "<verbatim snippet copied word-for-word from the article>",
              "location_hint": "<e.g., H2: 'Benefits', paragraph after first H3, list item #3>",
              "reason": "<why this should be improved>",
              "suggested_replacement": "<replacement text the user could paste 1:1>",
              "severity": "low|medium|high"
           ,
          "SEO":  [
            {
              "exact_text": "<verbatim snippet copied word-for-word from the article>",
              "location_hint": "<e.g., H2: 'Benefits', paragraph after first H3, list item #3>",
              "reason": "<why this should be improved>",
              "suggested_replacement": "<replacement text the user could paste 1:1>",
              "severity": "low|medium|high"
           
        }
      }`;

  const promptTemplate = `You are a bot that specializes in analyzing the content of a content creator's website and rating/repairing the quality of that content.
    Given an HTML article, make sure to give a rating from 1-100, on the following categories: Overall Rating, Readability, Topic Authority, Content Value, and SEO.
    Your Overall Rating should factor in the following key metrics: Relevance, Accuracy, Clarity and Readability, Depth and Detail, Originality, Structure and Organization, Engagement, and SEO.
    Your Readability Rating should factor in the following key metrics: Language and Vocabulary, Sentence Structure, Paragraph Structure, Logical Flow, Consistency, Readability Scores, Visual Aids, Typography, and Layout.
    Your Topic Authority Rating should factor in the following key metrics: Citation of Sources, Depth of Content, Accuracy and Fact-Checking, Currency of Information, Originality, and Relevance of Content. You should not include anything regarding Feedback and Engagement, Recognition and Endorsements, Balance and Fairness, and Expertise of the Author in your Topic Authority Rating.
    Your Content Value Rating should factor in the following key metrics: Utility, Informativeness, Relevance, Uniqueness, Depth and Detail, Actionability, Quality of Presentation, and Credibility. You should not include anything regarding Emotional Impact or Engagement in your Content Value Rating.
    Your SEO Rating should factor in the following key metrics: Keyword Optimization, Content Quality, Internal and External Linking, Image Optimization, Content Length, Schema Markup, and Meta Tags. You should not include anything regarding URL Structure, User Engagement, or Page Load Speed in your SEO Rating.
    Also, return the best SEO driven Meta Tags as a String, that you can come up with for the provided article. Meta Tags should be the optimal length and must have the appropriate "<" and ">" symbols.
    It is absolutely imperative that your answer be given exactly in the provided JSON format. Do not add anything outside of the braces of the JSON object.
    
    VERY IMPORTANT — IMPROVEMENT SNIPPETS:
    • For each category, return 0–5 specific issues.
    • "exact_text" MUST be copied verbatim from the provided HTML so it can be located exactly in the document later. Do not normalize quotes, punctuation, or whitespace. Do not return the html tags, just the text.
    • "suggested_replacement" MUST be a drop-in replacement for "exact_text" (same context/voice), not generic advice.
    • Provide a helpful "location_hint" (nearest heading text, section name, list position, etc.).
    • If there are no issues for a category, return an empty array [].

    Return ONLY a single JSON object matching the 'Format' exactly. Do not use backticks or add any commentary.
    
    Context: {context}
    Format: {format}`;

  const prompt = PromptTemplate.fromTemplate(promptTemplate);

  const chain = prompt.pipe(llm).pipe(new StringOutputParser());

  const response = await chain.invoke({
    context,
    format,
  });

  results = parseAiJson(response);
  console.log("AI Response: ", results);
  //console.log("Type of Meta Tags Response: ", typeof results["Meta Tags"]);

  setFeedback({
    overall: results["Overall Grade"],
    readability: results["Readability Grade"],
    topicAuthority: results["Topic Authority Grade"],
    contentValue: results["Content Value Grade"],
    seo: results["SEO Grade"],
    metaTags: results["Meta Tags"],
    improvements: results["Improvements"],
  });

  return results;

  // Add your analysis logic here
  // For example, you could parse the HTML, count words, analyze tags, etc.
};
